import { doc, setDoc } from "firebase/firestore";
import { useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorText, FieldLabel, LoadingView, PrimaryButton, SBCard, SecondaryButton } from "@/components/ui";
import { db } from "@/firebase/app";
import { useDoc } from "@/hooks/firestoreHooks";
import {
  FIELD_TYPE_LABELS,
  FIELD_TYPES,
  UNIVERSAL_ATHLETE_FIELD_ROLES,
  UNIVERSAL_ATHLETE_ROLE_LABELS,
  UNIVERSAL_ATHLETE_ROLE_SUGGESTED_LABELS,
  makeFieldForAthleteRole,
  sbotSlug,
  type LeagueAthleteFieldDefinition,
  type LeagueAthleteFieldPrivacyScope,
  type LeagueAthleteFieldType,
} from "@/models/fieldDefinition";
import { fieldSchemaFirestoreData, parseFieldSchema, type LeagueFieldSchema } from "@/models/schema";
import { humanActionAuditActor, recordActionAudit } from "@/services/actionAudit";
import "@/features/home/home.css";
import "@/features/league/league.css";

// Shared editor for leagueAthleteSchemas and leagueOrganizationAdminSchemas
// (both share validAthleteSchemaShape). Ports LeagueDataFieldEditor behavior.

export function SchemaEditor({
  collectionName,
  leagueID,
  sportID,
  title,
  subtitle,
}: {
  collectionName: "leagueAthleteSchemas" | "leagueOrganizationAdminSchemas";
  leagueID: string;
  sportID?: string;
  title: string;
  subtitle: string;
}) {
  const { currentUser } = useAuth();
  const remote = useDoc(doc(db, collectionName, leagueID), parseFieldSchema);
  const [fields, setFields] = useState<LeagueAthleteFieldDefinition[]>([]);
  const [version, setVersion] = useState(0);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const [newLabel, setNewLabel] = useState("");
  const [newType, setNewType] = useState<LeagueAthleteFieldType>("text");
  const [newScope, setNewScope] = useState<LeagueAthleteFieldPrivacyScope>("league");
  const [newRequired, setNewRequired] = useState(false);
  const [newOptions, setNewOptions] = useState("");

  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const isAthleteSchema = collectionName === "leagueAthleteSchemas";

  useEffect(() => {
    if (!hasLoaded && !remote.loading) {
      setFields(remote.data?.fields ?? []);
      setVersion(remote.data?.version ?? 0);
      setHasLoaded(true);
    }
  }, [remote.loading, remote.data, hasLoaded]);

  const addField = () => {
    const label = newLabel.trim();
    if (!label) return;
    const base = sbotSlug(label) || `field-${fields.length + 1}`;
    let id = base;
    let suffix = 2;
    const existing = new Set(fields.map((field) => field.id));
    while (existing.has(id)) {
      id = `${base}-${suffix}`;
      suffix += 1;
    }
    const requiresOptions = newType === "singleSelect" || newType === "multiSelect";
    const options = requiresOptions
      ? newOptions
          .split(",")
          .map((entry) => entry.trim())
          .filter(Boolean)
          .map((entry) => ({ id: sbotSlug(entry) || entry, label: entry }))
      : [];
    if (requiresOptions && options.length === 0) {
      setError("Select fields need at least one option (comma-separated).");
      return;
    }
    setFields((previous) => [
      ...previous,
      {
        id,
        label,
        type: newType,
        privacyScope: newScope,
        isRequired: newRequired,
        options,
        validation: { allowedMIMETypes: [] },
        sortOrder: previous.length ? Math.max(...previous.map((f) => f.sortOrder)) + 1 : 0,
        isArchived: false,
      },
    ]);
    setNewLabel("");
    setNewOptions("");
    setNewRequired(false);
    setIsDirty(true);
    setError(null);
  };

  const addUniversalAthleteField = (role: (typeof UNIVERSAL_ATHLETE_FIELD_ROLES)[number]) => {
    if (fields.some((field) => !field.isArchived && field.universalAthleteRole === role)) return;
    const sortOrder = fields.length ? Math.max(...fields.map((field) => field.sortOrder)) + 1 : 0;
    setFields((previous) => [...previous, makeFieldForAthleteRole(role, sortOrder, new Set(previous.map((field) => field.id)))]);
    setIsDirty(true);
    setError(null);
  };

  const updateField = (id: string, update: Partial<LeagueAthleteFieldDefinition>) => {
    setFields((previous) => previous.map((field) => (field.id === id ? { ...field, ...update } : field)));
    setIsDirty(true);
  };

  const moveField = (id: string, direction: -1 | 1) => {
    setFields((previous) => {
      const sorted = [...previous].sort((a, b) => a.sortOrder - b.sortOrder);
      const index = sorted.findIndex((field) => field.id === id);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= sorted.length) return previous;
      [sorted[index], sorted[target]] = [sorted[target], sorted[index]];
      return sorted.map((field, order) => ({ ...field, sortOrder: order }));
    });
    setIsDirty(true);
  };

  const save = async () => {
    setIsSaving(true);
    setError(null);
    setMessage(null);
    try {
      const schema: LeagueFieldSchema = {
        leagueID,
        sportID: remote.data?.sportID ?? sportID,
        version: version + 1,
        fields,
        createdAt: remote.data?.createdAt ?? new Date(),
        updatedAt: new Date(),
        updatedByUserID: currentUser?.id,
      };
      await setDoc(doc(db, collectionName, leagueID), fieldSchemaFirestoreData(schema));
      if (currentUser) {
        void recordActionAudit({
          before: remote.data ? fieldSchemaFirestoreData(remote.data) : undefined,
          after: fieldSchemaFirestoreData(schema),
          context: {
            leagueID,
            targetCollection: collectionName,
            targetDocumentID: leagueID,
            targetDisplayName: isAthleteSchema ? "Athlete Fields" : "Organization Admin Fields",
            actionType: remote.data ? "update" : "create",
            actor: humanActionAuditActor(currentUser),
          },
        });
      }
      setVersion(schema.version);
      setIsDirty(false);
      setMessage(`Saved (version ${schema.version}).`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!hasLoaded) return <LoadingView />;

  const sortedFields = [...fields].sort((a, b) => a.sortOrder - b.sortOrder);
  const mappedAthleteRoles = new Set(fields.filter((field) => !field.isArchived).map((field) => field.universalAthleteRole));

  return (
    <SBCard className="home-card league-schema">
      <div className="home-card-head">
        <div>
          <h3>{title}</h3>
          <p className="home-hint">{subtitle}</p>
        </div>
        <span className="badge">v{version}</span>
      </div>

      {isAthleteSchema && (
        <div style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          <div className="home-card-head">
            <div>
              <h3>Universal Athlete Fields</h3>
              <p className="home-hint">Mapped participant fields for broadcast and graphics data.</p>
            </div>
            <span className="badge">{UNIVERSAL_ATHLETE_FIELD_ROLES.length}</span>
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {UNIVERSAL_ATHLETE_FIELD_ROLES.map((role) => {
              const isMapped = mappedAthleteRoles.has(role);
              return (
                <div key={role} className="event-row league-management-row" style={{ cursor: "default", opacity: isMapped ? 0.65 : 1 }}>
                  <div className="event-row-main">
                    <strong>{UNIVERSAL_ATHLETE_ROLE_LABELS[role]}</strong>
                    <span>{UNIVERSAL_ATHLETE_ROLE_SUGGESTED_LABELS[role]}</span>
                  </div>
                  <div className="event-row-badges league-row-actions">
                    {isMapped && <span className="badge">Mapped</span>}
                    <SecondaryButton
                      title={isMapped ? "Added" : "Add"}
                      onClick={() => addUniversalAthleteField(role)}
                      disabled={isMapped}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {sortedFields.map((field) => (
        <div key={field.id} className="event-row league-management-row" style={{ cursor: "default", opacity: field.isArchived ? 0.5 : 1 }}>
          <div className="event-row-main">
            <strong>
              {field.label}
              {field.isRequired ? " *" : ""}
            </strong>
            <span>
              {FIELD_TYPE_LABELS[field.type]} · {field.privacyScope}
              {field.options.length > 0 ? ` · ${field.options.map((o) => o.label).join(", ")}` : ""}
              {field.universalAthleteRole ? ` · Universal: ${UNIVERSAL_ATHLETE_ROLE_LABELS[field.universalAthleteRole]}` : ""}
            </span>
          </div>
          <div className="event-row-badges league-row-actions">
            <button type="button" className="sb-button-secondary league-move-button" aria-label={`Move ${field.label} up`} onClick={() => moveField(field.id, -1)}>↑</button>
            <button type="button" className="sb-button-secondary league-move-button" aria-label={`Move ${field.label} down`} onClick={() => moveField(field.id, 1)}>↓</button>
            <SecondaryButton
              title={field.isArchived ? "Restore" : "Archive"}
              destructive={!field.isArchived}
              onClick={() => updateField(field.id, { isArchived: !field.isArchived })}
            />
          </div>
        </div>
      ))}
      {sortedFields.length === 0 && <p className="home-hint">No fields defined yet.</p>}

      <div style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
        <h3 style={{ fontSize: 15 }}>Add field</h3>
        <div className="league-schema-form">
          <FieldLabel label="Label"><input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} /></FieldLabel>
          <FieldLabel label="Field type"><select value={newType} onChange={(e) => setNewType(e.target.value as LeagueAthleteFieldType)}>
            {FIELD_TYPES.map((type) => (
              <option key={type} value={type}>
                {FIELD_TYPE_LABELS[type]}
              </option>
            ))}
          </select></FieldLabel>
          <FieldLabel label="Privacy"><select value={newScope} onChange={(e) => setNewScope(e.target.value as LeagueAthleteFieldPrivacyScope)}>
            <option value="public">Public</option>
            <option value="organization">Organization</option>
            <option value="league">League</option>
          </select></FieldLabel>
        </div>
        {(newType === "singleSelect" || newType === "multiSelect") && (
          <FieldLabel label="Options (comma-separated)"><input value={newOptions} onChange={(e) => setNewOptions(e.target.value)} /></FieldLabel>
        )}
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14 }}>
          <input type="checkbox" checked={newRequired} onChange={(e) => setNewRequired(e.target.checked)} style={{ width: "auto" }} />
          Required
        </label>
        <SecondaryButton title="Add field" onClick={addField} disabled={!newLabel.trim()} />
      </div>

      <PrimaryButton title={isSaving ? "Saving…" : "Save schema"} onClick={() => void save()} disabled={isSaving || !isDirty} />
      <ErrorText>{error}</ErrorText>
      {message && <p className="home-hint">{message}</p>}
      <ErrorText>{remote.error}</ErrorText>
    </SBCard>
  );
}

export function useSchemaScreenLeague() {
  // Small helper hook so the two schema screens share the league resolution.
  return null;
}
